import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { Component, inject, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { AgentService } from '../../services/agent.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Loader } from '../../loader/loader';
import { ReportService } from '../../services/report.service';

import { ReportStatusRequest } from '../../models/report-status-request';
import { SnackbarService } from '../../services/snackbar.service';
import { CommentRequest } from '../../models/comment-request';
import { AgentRequest } from '../../models/agent-models/agent-request';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatDialogModule } from '@angular/material/dialog';
import { NewSectionRequest } from '../../models/new-section-request';
import { DialogueBox } from '../../dialogue-box/dialogue-box';

import { SectionValidationRequest } from '../../models/section-validation-request';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../auth/auth.service';

import { AngularTiptapEditorComponent } from '@flogeez/angular-tiptap-editor';
import { ReportOutline } from '../../components/report-outline/report-outline';
import { ReportScores } from '../../components/report-scores/report-scores';
import { StorageService } from '../../auth/storage.service';
import { DialogueTexts } from '../../constants/dialog-texts.constant';
import { ValidationMessagesTexts } from '../../constants/validation-messages.constants';
import { ReportStatus } from '../../models/report';
import { ApiResponseMessages } from '../../constants/api-response-messages.constants';
import { UserRolePermissionResponse } from '../../models/user-roles-permissions';
import { SearchableDropdown } from '../../components/searchable-dropdown/searchable-dropdown';
import { Labels } from '../../constants/labels-constant';

interface Section {
  id: number;
  sectionNumber: string;
  name: string;
  content: string;
  isEditing: boolean;
  isNew?: boolean;
  originalState?: { name: string; content: string };
  revisionRequested?: number;
  parentSectionId?: number | null;
}
interface TocNode {
  id: string;
  name: string;
  children?: TocNode[];
}

@Component({
  selector: 'app-report-sections',
  standalone: true,
  imports: [
    CommonModule,
    MatTreeModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDialogModule,
    FormsModule,
    Loader,
    DialogueBox,
    AngularTiptapEditorComponent,
    ReportOutline,
    ReportScores,
    ReactiveFormsModule,
    SearchableDropdown,
  ],
  templateUrl: './report-sections.html',
  styleUrl: './report-sections.scss',
  providers: [],
})
export class ReportSections implements OnInit {
  private storageService = inject(StorageService);
  private agentService = inject(AgentService);
  private reportService = inject(ReportService);
  private snackbar = inject(SnackbarService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);

  roles: string[] = [];
  referenceFiles: string[] = [];
  responseData: any;
  initialGenerate: boolean = false;
  treeControl = new NestedTreeControl<TocNode>((node) => node.children);
  showScoreBoard = false;
  notesStatus = false;
  notesClosing = false;
  isTreeCollapsed = false;
  dataSource = new MatTreeNestedDataSource<TocNode>();
  isLoading = false;
  outlineAgentData: any = {};
  scoreData: any = {};
  qualityScore: any = [];
  overAllQualityScore: string | null = null;
  isJuniorScientist: boolean = false;
  isSeniorScientist: boolean = false;
  apiResponse = {};
  sectionsData: Section[] = [];
  status!: number;
  selectedSectionNumber: string | '' = '';
  clientName: string = '';
  locationName: string = '';
  reportId: number = 0;
  isDialogOpen = false;
  isReferenceDialogOpen = false;
  isClosing = false;
  isReferenceClosing = false;
  isDeleteDialogOpen = false;
  isReworkDialogOpen = false;
  isReportStatusDialogOpen = false;
  sectionNumberList: any = [];
  deleteSectionRequest: NewSectionRequest | null = null;
  selectedSectionId: number | null = null;
  addAsChild: boolean = false;
  newSectionContent: string = '';
  newSectionTitle: string = '';
  selectedSectionIdForComments: number | 0 = 0;
  initials: string = '';
  name: string = '';
  reportStatusDialogButtonText = '';
  reportStatusDialogMessage = '';
  checkedSections = new Set<number>();
  reportStatus: number | null = null;

  dialogTexts = DialogueTexts;
  labels = Labels;
  validationMessages = ValidationMessagesTexts;
  apiResponseMessages = ApiResponseMessages;
  reportStatusCodes = ReportStatus;
  reworkSectionIds: number[] = [];
  private reworkInterval: any;

  openDialog() {
    this.isDialogOpen = true;
    this.isClosing = false;
  }

  closeDialog() {
    this.isClosing = true;

    setTimeout(() => {
      this.isDialogOpen = false;
      this.isClosing = false;
      this.addAsChild = false;
      this.newSectionContent = '';
      this.newSectionTitle = '';
      this.selectedSectionId = null;
    }, 300);
  }

  openDeleteDialog(section: Section) {
    this.isDeleteDialogOpen = true;
    const request: NewSectionRequest = {
      id: section.id,
      reportId: this.reportId,
      sectionTitle: null,
      sectionContent: null,
      isSubSection: false,
    };
    this.deleteSectionRequest = request;
  }

  closeDeleteDialog() {
    this.isDeleteDialogOpen = false;
  }

  closeReportStatusDialog() {
    this.isReportStatusDialogOpen = false;
    this.router.navigate(['/reports']);
  }

  ngOnInit(): void {
    this.authService.getCurrentAccount().then((currentAccount) => {
      if (currentAccount) {
        this.name = currentAccount.name || '';
        const parts = this.name.split(/\s+/).filter(Boolean).slice(0, 2);
        this.name = parts.reverse().join(' ').replace(/,$/, '').trim();
        this.initials = parts.map((p) => p[0].toUpperCase()).join('');
      }
    });
     const storageUser = this.storageService.get('user');
     const user = JSON.parse(storageUser) as UserRolePermissionResponse;
     this.roles = user.roles ?? [];
     this.isJuniorScientist = this.roles?.includes('JrScientist');
     this.isSeniorScientist = this.roles?.includes('SrScientist');
     this.initialGenerate = history.state?.initialGenerate;

    if (!this.initialGenerate) {
      this.route.queryParams.subscribe((params) => {
        this.reportId = params['reportId'];
      });
      this.checkReportStatus(this.reportId);
    } else {
      this.reportId = history.state?.data;
      this.reportName = history.state?.reportData.reportName;
      this._invokeOrchestratorAgent(history.state?.data, false);
    }
  }

  hasChild = (_: number, node: TocNode) => !!node.children?.length;
  step = signal(3);
  setStep(newStep: number) {
    console.log('step', this.step);
    this.step.set(newStep);
  }

  reportName: any;
  notes: any[] = [];
  newComment: string = '';

  _invokeOrchestratorAgent(reportId: number, rework: boolean) {
    this.isLoading = true;
    const request: AgentRequest = {
      report_id: reportId.toString()
    };
    this.agentService.invokeOrchestrationAgent(request).subscribe({
      next: (response) => {
        if (response.status.toLocaleLowerCase() === 'inprogress') {
          this.isLoading = false;
          let message;
          if (rework) {
            message = this.apiResponseMessages.SECTION_SENT_REWORK;
          } else {
            message = this.apiResponseMessages.REPORT_GENERATION_INITIATED;
          }
          this.snackbar.showCustom({
            type: 'success',
            title: this.validationMessages.TOASTER.SUCCESS.TITLE,
            message: message,
          });
          this.checkReportStatus(reportId);
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message,
          });
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
        this.isLoading = false;
      },
    });
  }

  checkReportStatus(reportId: number) {
    this.isLoading = true;
    this.isReportStatusDialogOpen = false;
    this.reportService.getReportGenerationStatus(reportId).subscribe({
      next: (response) => {
        if (response.success && response.data !== 0 && response.data !== 1 && response.data !== 6) {
          this.status = response.data;
          this.isLoading = false;
          this.fetchSectionList(reportId, true);
        } else if (response.data === 1) {
          this.reportStatusDialogButtonText = this.dialogTexts.REPORT_STATUS.CONFIRM;
          this.reportStatusDialogMessage = this.dialogTexts.REPORT_STATUS.MESSAGE;
          this.isReportStatusDialogOpen = true;
          this.isLoading = false;
        } else {
          this.isReportStatusDialogOpen = true;
          this.reportStatusDialogButtonText = this.dialogTexts.REPORT_STATUS.REGENERATE;
          this.reportStatusDialogMessage = this.dialogTexts.REPORT_STATUS.REGENERATE_MESSAGE;
          this.isLoading = false;
        }
        this.reportStatus = response.data;
      },
      error: (err) => {
        this.isLoading = false;
        if (err.status === 404) {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        }
      },
    });
  }

  handleReportConfirm(reportId: number) {
    if (this.reportStatus === 1) {
      this.checkReportStatus(reportId);
    } else {
      this._invokeOrchestratorAgent(reportId, false);
    }
  }

  redirectTohome() {
    this.router.navigate(['/reports']);
  }

  clearComment() {
    this.newComment = '';
  }

  openNotesPopup(sectionNumber: string, sectionId: number) {
    this.selectedSectionNumber = sectionNumber;
    this.selectedSectionIdForComments = sectionId;
    this.notesClosing = false;
    this.notesStatus = true;
  }

  closeNotesPopup() {
    this.notesClosing = true;
    setTimeout(() => {
      this.notesStatus = false;
      this.notesClosing = false;
    }, 250);
  }

  postComment() {
    this.isLoading = true;
    if (!this.newComment?.trim()) {
      this.isLoading = false;
      return;
    }

    if (!Array.isArray(this.notes)) {
      this.notes = [];
    }

    const newNote = {
      id: this.notes.length + 1,
      initials: this.initials,
      name: this.name,
      section_number: this.selectedSectionNumber,
      timestamp: 'Just now',
      comment: this.newComment.trim(),
      bgColor: '#1C8768',
    };

    this.notes.push(newNote);

    this.saveComment();
  }

  saveComment() {
    if (!this.newComment?.trim()) {
      this.isLoading = false;
      return;
    }
    const request: CommentRequest = {
      reportId: this.reportId,
      sectionId: this.selectedSectionIdForComments,
      comments: this.newComment.trim(),
      bgColor: '#1C8768',
      section_Number: this.selectedSectionNumber,
    };
    this.isLoading = true;
    this.reportService.updateCommentSection(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.newComment = '';
          this.fetchSectionList(this.reportId, true);
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message,
          });
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
      },
    });
  }

  sendForApproval(status: number) {
    this.isLoading = true;
    const request: ReportStatusRequest = {
      reportId: this.reportId,
      status: status,
    };

    this.reportService.sendReportForApproval(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackbar.showCustom({
            type: 'success',
            title: this.validationMessages.TOASTER.SUCCESS.TITLE,
            message: response.message,
          });
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate(['/reports']);
          });
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        }
        this.isLoading = false;
      },
    });
  }

  fetchSectionList(reportId: number, showLoader: boolean) {
    this.isLoading = showLoader;
    this.reportService.getSectionList(reportId).subscribe({
      next: (response) => {
        if (response.success) {
          this.sectionNumberList = response.data.sectionList.map((section) => ({
            ...section,
            displayText: `${
              section.sectionNumber && section.sectionNumber !== '0.0'
                ? section.sectionNumber + ' '
                : ''
            }${section.sectionTitle || ''}`,
          }));

          this.reportName = response.data.reportName;
          this.clientName = response.data.clientName;
          this.locationName = response.data.siteName;
          this.sectionsData = this.extractSections(response.data.sectionList);

          this.reworkSectionIds = this.sectionsData
            .filter((sec) => sec.revisionRequested === 2)
            .map((sec) => sec.id);
          this.startReworkPolling();

          this.dataSource.data = this.buildTocTree(response.data.sectionList);
          this.overAllQualityScore = response.data.averageQualityScore;
          // Initialize checked sections based on validationRequested flag
          this.checkedSections.clear();
          this.notes = (response.data.commentHistory as any[]).map((note) => {
            const section = response.data.sectionList.find((x) => x.id === note.sectionId);
            const secNum = section?.sectionNumber;
            const showSectionNumber = secNum && secNum !== 0 && secNum !== 0.0;
            return {
              ...note,
              updatedDate: this.parseUtcStringToDate(note.updatedDate),
              sectionNumber: showSectionNumber ? secNum : null,
              sectionTitle: section?.sectionTitle || null,
            };
          });

          this.sectionsData.forEach((section) => {
            if (section.revisionRequested === 2) {
              this.checkedSections.add(section.id);
            }
          });
          //bind section score
          this.qualityScore = response.data.sectionList
            .filter((item: any) => item.parentSectionId === null)
            .map((item: any) => ({
              section:
                item.sectionNumber && item.sectionNumber !== '0.0'
                  ? `${item.sectionNumber} ${item.sectionTitle}`
                  : item.sectionTitle,
              score: Number(item.sectionScore),
              revisionRequested: Number(item.validationRequested),
              showBotProgress:item.showBotProgress
            }));
          this.referenceFiles = response.data.reportReferences;
          this.getReworkSectionList();

          this.showScoreBoard = true;
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message,
          });
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
      },
    });
  }

  startReworkPolling() {
    this.reworkInterval = setInterval(() => {
      if (this.reworkSectionIds.length === 0) {
        clearInterval(this.reworkInterval);
        this.reworkInterval = null;
        return;
      }
      this.getReworkSectionList();
    }, 15000);
  }

  getReworkSectionList() {
    if (!this.reworkSectionIds || this.reworkSectionIds.length === 0) {
      return;
    }
    const request: SectionValidationRequest = {
      reportId: this.reportId,
      sectionIds: this.reworkSectionIds,
    };
    this.reportService.getReworkSectionList(request).subscribe({
      next: (response) => {
        if (response.success) {
          let reworkSectionData = this.extractSections(response.data.sectionList);
          reworkSectionData.forEach((rework: any) => {
            const index = this.sectionsData.findIndex((sec) => sec.id === rework.id);
            if (index !== -1) {
              this.sectionsData[index] = {
                ...this.sectionsData[index],
                ...rework,
              };
            }
          });

          let reworkQualityScore = response.data.sectionList
            .filter((item: any) => item.parentSectionId === null)
            .map((item: any) => ({
              section:
                item.sectionNumber && item.sectionNumber !== '0.0'
                  ? `${item.sectionNumber} ${item.sectionTitle}`
                  : item.sectionTitle,
              score: Number(item.sectionScore),
              revisionRequested: Number(item.validationRequested),
            }));

          reworkQualityScore.forEach((rework: any) => {
            const index = this.qualityScore.findIndex((q: any) => q.section === rework.section);
            if (index !== -1) {
              this.qualityScore[index] = {
                ...this.qualityScore[index],
                ...rework,
              };
            }
          });

          //stop pooling
          this.reworkSectionIds = this.sectionsData
            .filter((sec) => sec.revisionRequested === 2)
            .map((sec) => sec.id);
          if (this.reworkSectionIds.length === 0 && this.reworkInterval) {
            clearInterval(this.reworkInterval);
            this.reworkInterval = null;
            this.checkedSections.clear();
            this.fetchSectionList(this.reportId, false);
          }
        }
      },
    });
  }

  openReferrenceDialog() {
    this.isReferenceDialogOpen = true;
  }

  closeReferrenceDialog() {
    this.isReferenceClosing = true;

    setTimeout(() => {
      this.isReferenceDialogOpen = false;
      this.isReferenceClosing = false;
    }, 300);
  }

  parseUtcStringToDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const hasTz = /[zZ]|[+\-]\d{2}:\d{2}$/.test(value);
    const normalized = hasTz ? value : value + 'Z';
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  }

  editSection(section: Section) {
    section.originalState = { name: section.name, content: section.content };
    section.isEditing = true;
  }

  saveSection(section: Section) {
    if (!section.id) {
      this.snackbar.showCustom({
        type: 'error',
        title: this.validationMessages.TOASTER.ERROR.TITLE,
        message: this.validationMessages.REPORT_SECTION.SECTION_NUMBER_REQUIRED,
      });
      return;
    }
    if (!section.name || section.name.trim() === '') {
      this.snackbar.showCustom({
        type: 'error',
        title: this.validationMessages.TOASTER.ERROR.TITLE,
        message: this.validationMessages.REPORT_SECTION.SECTION_NUMBER_REQUIRED,
      });
      return;
    }

    this.isLoading = true;
    section.isEditing = false;
    delete section.originalState;
    const request: NewSectionRequest = {
      id: section.id,
      reportId: this.reportId,
      sectionContent: section.content,
      sectionTitle: section.name,
      isSubSection: null,
    };

    this.reportService.updateSection(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackbar.showCustom({
            type: 'success',
            title: this.validationMessages.TOASTER.SUCCESS.TITLE,
            message: this.apiResponseMessages.SECTION_UPDATED,
          });
          this.fetchSectionList(this.reportId, true);
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: this.apiResponseMessages.FAILED_TO_UPDATE_SECTION,
          });
        }
      },
      error: (err) => {
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  addNewSection() {
    if (!this.selectedSectionId || this.selectedSectionId === 0) {
      this.snackbar.showCustom({
        type: 'error',
        title: this.validationMessages.TOASTER.ERROR.TITLE,
        message: this.validationMessages.REPORT_SECTION.SECTION_REQUIRED,
      });
      return;
    }
    if (!this.newSectionTitle || this.newSectionTitle.trim() === '') {
      this.snackbar.showCustom({
        type: 'error',
        title: this.validationMessages.TOASTER.ERROR.TITLE,
        message: this.validationMessages.REPORT_SECTION.SECTION_TITLE_REQUIRED,
      });
      return;
    }
    this.isLoading = true;
    const request: NewSectionRequest = {
      id: this.selectedSectionId,
      reportId: this.reportId,
      sectionContent: this.newSectionContent,
      sectionTitle: this.newSectionTitle,
      isSubSection: this.addAsChild,
    };
    this.reportService.addNewSection(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.fetchSectionList(this.reportId, true);
          this.snackbar.showCustom({
            type: 'success',
            title: this.validationMessages.TOASTER.SUCCESS.TITLE,
            message: this.apiResponseMessages.SECTION_ADDED,
          });
          this.closeDialog();
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: this.apiResponseMessages.FAILED_TO_ADD_SECTION,
          });
        }
      },
      error: (err) => {
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  cancelEdit(section: Section) {
    if (section.originalState) {
      section.name = section.originalState.name;
      section.content = section.originalState.content;
    }
    section.isEditing = false;
    delete section.originalState;
  }

  deleteSection(): void {
    if (!this.deleteSectionRequest) {
      this.isLoading = false;
      return;
    }
    this.isDeleteDialogOpen = false;
    this.isLoading = true;
    this.reportService.deleteSection(this.deleteSectionRequest).subscribe({
      next: (response) => {
        if (response.success) {
          this.fetchSectionList(this.reportId, true);
          this.snackbar.showCustom({
            type: 'success',
            title: this.validationMessages.TOASTER.SUCCESS.TITLE,
            message: this.apiResponseMessages.SECTION_DELETED,
          });
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: this.apiResponseMessages.FAILED_TO_DELETE_SECTION,
          });
        }
      },
      error: (err) => {
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  insertSection(index: number) {
    const newSection: Section = {
      id: 0,
      sectionNumber: '',
      name: 'New Section',
      content: '',
      isNew: true,
      isEditing: true,
    };
    this.sectionsData.splice(index, 0, newSection);
  }

  checkSectionSelctionStatus() {
    const selectedSectionIds = this.sectionsData
      .filter((section) => this.isSectionChecked(section.id))
      .map((section) => section.id);

    if (selectedSectionIds.length === 0) {
      this.isReworkDialogOpen = true;
      return;
    }
    this.sendToAgentForRework(selectedSectionIds);
  }

  sendToAgentForRework(selectedParentIds: number[] = []) {
    this.isLoading = true;
    const request: SectionValidationRequest = {
      reportId: this.reportId,
      sectionIds: selectedParentIds,
    };
    this.isReworkDialogOpen = false;
    this.reportService.updateSectionValidationStatus(request).subscribe({
      next: (response) => {
        if (response.success) {
          this._invokeOrchestratorAgent(this.reportId, true);
        } else {
          this.snackbar.showCustom({
            type: 'error',
            title: this.validationMessages.TOASTER.ERROR.TITLE,
            message: response.message,
          });
        }
        this.fetchSectionList(this.reportId, true);
      },
      error: (err) => {
        this.snackbar.showCustom({
          type: 'error',
          title: this.validationMessages.TOASTER.ERROR.TITLE,
          message: this.validationMessages.TOASTER.ERROR.SOMETHING_WENT_WRONG,
        });
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  closeReworkDialog() {
    this.isReworkDialogOpen = false;
  }
  openReworkDialog() {
    this.isReworkDialogOpen = true;
  }

  getUserColor(userName: string): string {
    const colors = ['#E52713', '#1C8768', '#FFC107', '#008768', '#AECC53', '#00353E'];

    if (!userName) return colors[0];

    let hash = 0;
    for (let i = 0; i < userName.length; i++) {
      hash = userName.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  scrollTo(sectionId: string) {
    const element = document.getElementById('sectionContainer-' + sectionId);
    if (!element) return;

    const container =
      (element.closest('.section-scroll') as HTMLElement | null) ||
      (document.scrollingElement as HTMLElement);
    const header = container?.querySelector('.hearder-part') as HTMLElement | null;
    const headerHeight = header ? header.offsetHeight : 0;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const offset = Math.max(
      0,
      elementRect.top - containerRect.top + container.scrollTop - headerHeight - 8,
    );

    container.scrollTo({ top: offset, behavior: 'smooth' });

    if (this.isTOCOverlayOpen) {
      this.closeTOCOverlay();
    }
  }

  private extractSections(sections: any[]): Section[] {
    const sortedSections = sections.sort((a, b) => a.sectionSequence - b.sectionSequence);
    return sortedSections.map((sec) => ({
      id: sec.id,
      sectionNumber:
        sec.sectionNumber === '0.0' || sec.sectionNumber == null ? '' : sec.sectionNumber,
      name: sec.sectionTitle || '',
      content: sec.sectionContent || '',
      isEditing: false,
      revisionRequested: sec.revisionRequested,
    }));
  }

  private buildTocTree(sections: any[]): TocNode[] {
    const sectionMap = new Map<number, TocNode>();
    sections.forEach((section) => {
      sectionMap.set(section.id, {
        id: section.id,
        name:
          section.sectionNumber && section.sectionNumber !== '0.0'
            ? `${section.sectionNumber} ${section.sectionTitle}`
            : `${section.sectionTitle}`,
        children: [],
      });
    });

    const tocRoots: TocNode[] = [];
    sections.forEach((section) => {
      const currentNode = sectionMap.get(section.id);
      if (!currentNode) return;

      if (section.parentSectionId) {
        const parentNode = sectionMap.get(section.parentSectionId);
        if (parentNode && parentNode.children) {
          parentNode.children.push(currentNode);
        }
      } else {
        tocRoots.push(currentNode);
      }
    });

    const sortBySequence = (nodes: TocNode[]) => {
      nodes.sort((a, b) => {
        const seqA = sections.find((s) => s.id === a.id)?.sectionSequence ?? 0;
        const seqB = sections.find((s) => s.id === b.id)?.sectionSequence ?? 0;
        return seqA - seqB;
      });
      nodes.forEach((n) => sortBySequence(n.children || []));
    };

    sortBySequence(tocRoots);
    return tocRoots;
  }

  isSectionChecked(sectionId: number): boolean {
    return this.checkedSections.has(sectionId);
  }

  onSectionCheckboxChange(section: Section, checked: boolean): void {
    if (checked) {
      this.checkedSections.add(section.id);
    } else {
      this.checkedSections.delete(section.id);
    }
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0][0].toUpperCase();
    }
    return (parts[0][0] || '').toUpperCase() + (parts[parts.length - 1][0] || '').toUpperCase();
  }

  // Direct TOC overlay (not using MatDialog)
  isTOCOverlayOpen = false;

  openTOC() {
    this.isTOCOverlayOpen = true;
    document.body.classList.add('overlay-dialog-open');
  }

  closeTOCOverlay() {
    this.isTOCOverlayOpen = false;
    document.body.classList.remove('overlay-dialog-open');
  }

  isScoresDialogOpen = false;
  openScores() {
    this.isScoresDialogOpen = true;
    document.body.classList.add('overlay-dialog-open');
  }

  closeScores() {
    this.isScoresDialogOpen = false;
    document.body.classList.remove('overlay-dialog-open');
  }
}
